import type { VisitSource } from "@rosc/contracts";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AskAssistant } from "./useAskAssistant";

// Up to this many files are named in the hint, the status and the toast.
export const NAMED_FILES = 3;

// "Fill from attached files": the files in use, without those whose add was
// undone (staging check 03/11: an undone file was read again).
export const filesToFill = (
  inUse: readonly number[],
  undone: ReadonlySet<number>,
): number[] => inUse.filter((id) => !undone.has(id));

// What the clinician calls a file: the name it was picked with in this
// session, else its title (a file attached elsewhere, or before a reload).
export const fileLabel = (
  source: Pick<VisitSource, "id" | "title">,
  names?: ReadonlyMap<number, string>,
): string => names?.get(source.id)?.trim() || source.title;

// The files the hint offers to add: attached in this session or whose add
// was undone, in use, not added since and not dismissed.
export const filesToOffer = ({
  sources,
  fresh,
  isIncluded,
  added,
  undone,
  dismissed,
}: {
  sources: readonly VisitSource[];
  fresh: ReadonlySet<number>;
  isIncluded: (id: number) => boolean;
  added: ReadonlySet<number>;
  undone: ReadonlySet<number>;
  dismissed: ReadonlySet<number>;
}): VisitSource[] =>
  sources.filter(
    (source) =>
      (fresh.has(source.id) || undone.has(source.id)) &&
      isIncluded(source.id) &&
      !added.has(source.id) &&
      !dismissed.has(source.id),
  );

// Pre-chart drafts (clinical lead, 04/11/2026): an attached file waits in
// the tray until the clinician picks how to add it, from the hint above the
// Ask bar or the file's preview: "Spread into sections" (into the template's
// headings, an earlier consult note summarized under the history heading)
// or "Summarize and insert" (a short summary under the heading they pick).
// Either edit applies itself with Undo (useAskAssistant `sendAuto`).
export const usePreChartFiles = ({
  enabled,
  sources,
  loading,
  isIncluded,
  names,
  ask,
}: {
  enabled: boolean;
  sources: readonly VisitSource[] | undefined;
  loading: boolean;
  isIncluded: (id: number) => boolean;
  // File names picked in this session, by source id. Shown in the hint, the
  // status and the toast only: a file name can identify the patient, so the
  // assistant gets the file's title and id, never its name.
  names?: ReadonlyMap<number, string>;
  ask: Pick<AskAssistant, "sendAuto" | "addedSources" | "undoneSources">;
}): {
  // The files the hint offers (empty when there is nothing to add).
  offered: VisitSource[];
  spread: (files: readonly VisitSource[]) => boolean;
  summarize: (files: readonly VisitSource[], heading: string | null) => boolean;
  dismiss: (files: readonly VisitSource[]) => void;
  label: (files: readonly VisitSource[]) => string;
} => {
  const { t } = useTranslation();
  // The files on the visit when the list first loaded: not offered.
  const seen = useRef<Set<number> | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<number>>(() => new Set());
  const [dismissed, setDismissed] = useState<ReadonlySet<number>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!enabled || loading || !sources) return;
    if (seen.current === null) {
      seen.current = new Set(sources.map((source) => source.id));
      return;
    }
    const known = seen.current;
    const added = sources.filter((source) => !known.has(source.id));
    if (!added.length) return;
    for (const source of added) known.add(source.id);
    setFresh((ids) => new Set([...ids, ...added.map((source) => source.id)]));
  }, [enabled, loading, sources]);

  // An Undo offers the file again, even after the hint was dismissed.
  const undone = ask.undoneSources;
  useEffect(() => {
    setDismissed((ids) =>
      [...ids].some((id) => undone.has(id))
        ? new Set([...ids].filter((id) => !undone.has(id)))
        : ids,
    );
  }, [undone]);

  const offered =
    enabled && sources
      ? filesToOffer({
          sources,
          fresh,
          isIncluded,
          added: ask.addedSources,
          undone,
          dismissed,
        })
      : [];

  const label = (files: readonly VisitSource[]): string =>
    files.length <= NAMED_FILES
      ? files.map((file) => fileLabel(file, names)).join(", ")
      : t("preChart.fileAdd.files", { count: files.length });
  // Each file by its title and its SOURCE number (the header it has in the
  // request): files with the same title, such as several photos, are still
  // told apart.
  const titles = (files: readonly VisitSource[]): string =>
    files.map((file) => `"${file.title}" (SOURCE ${file.id})`).join(", ");
  // Each reads only its own files, with the draft's lab choice.
  const scope = (files: readonly VisitSource[]) => ({
    sourceIds: files.map((file) => file.id),
    preChartLabs: true,
  });

  const spread = (files: readonly VisitSource[]): boolean =>
    files.length > 0 &&
    ask.sendAuto(
      t("preChart.fileAdd.spreadInstruction", {
        count: files.length,
        names: titles(files),
      }),
      label(files),
      { ...scope(files), preChartAdd: { mode: "spread" } },
    );
  const summarize = (
    files: readonly VisitSource[],
    heading: string | null,
  ): boolean =>
    files.length > 0 &&
    ask.sendAuto(
      heading
        ? t("preChart.fileAdd.summaryInstruction", {
            count: files.length,
            names: titles(files),
            heading,
          })
        : t("preChart.fileAdd.summaryInstructionNoHeading", {
            count: files.length,
            names: titles(files),
          }),
      label(files),
      {
        ...scope(files),
        preChartAdd: { mode: "summary", ...(heading ? { heading } : {}) },
      },
    );
  const dismiss = (files: readonly VisitSource[]): void =>
    setDismissed((ids) => new Set([...ids, ...files.map((file) => file.id)]));
  return { offered, spread, summarize, dismiss, label };
};
